import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";

import { useCart } from "../../context/CartContext";
import { useLocation } from "react-router-dom";

import MenuBar from "../../components/MenuBar/MenuBar";
import CategorySidebar from "../../components/CategorySidebar/CategorySidebar";
import CategoryProducts from "../../components/CategoryProducts/CategoryProducts";
import HeroCarousel from "../../components/HeroCarousel/HeroCarousel";
import CustomCakeModal from "../../components/CustomCakeModal/CustomCakeModal";
import IngredientsSection from "../../components/IngredientsSection/IngredientsSection";
import ContactInfo from "../../components/ContactInfo/ContactInfo";
import ProductDetailsModal from "../../components/ProductDetailsModal/ProductDetailsModal";

import {
  PageWrap,
  MenuLayout,
  LeftCol,
  RightCol,
  DesktopOnly,
} from "./MenuPageStyles";

const GET_MENU = gql`
  query GetMenu($categorii: [String!]!) {
    categoriesWithCount {
      id
      name
      count
    }
    productsByCategories(categorii: $categorii) {
      id
      categorie
      nume
      descriere
      descriere_lunga
      ingrediente
      alergeni
      calorii_kcal
      proteine_g
      carbohidrati_g
      grasimi_g
      sare_g
      zaharuri_g
      pret
      ordine
      imagine
      imagini {
        id
        image_url
        ordine
      }
      disponibil
      stoc
      tip_pret
      gramaj
      gramaj_selectabil
      gramaj_min
      gramaj_max
      pas_gramaj
      termen_ore
      reducere_procent
      reducere_tip
      pret_redus
      permite_mentiuni
      este_tort_personalizat
      afiseaza_pret_variabil
      afiseaza_per_buc
    }
  }
`;

const CATEGORY_ORDER = [
  "cadouri_dulci",
  "cafea_ceai",
  "desert_pahar",
  "patiserie",
  "platouri_evenimente",
  "platouri_post",
  "prajituri_artizanale",
  "tasting_box",
  "torturi",
];

export default function MenuPage() {
  const brand = "Ahava Cakes";
  const address = "Strada Elena Ghiba Birta 6-8, 310030 Arad";
  const mapsUrl =
    "https://www.google.com/maps/place/Ahava+Cakes+-+Cofet%C4%83rie+Arad/@46.1803242,21.315255,17z/data=!3m1!4b1!4m6!3m5!1s0x474599150cbff7bd:0xb27805b588efb352!8m2!3d46.1803242!4d21.315255!16s%2Fg%2F11sf72cqjn?entry=ttu&g_ep=EgoyMDI2MDIyNS4wIKXMDSoASAFQAw%3D%3D";
  const phone = "+40 775 107 107";
  const email = "office@ahavacakes.ro";

  const location = useLocation();
  const schedule = useMemo(
    () => [
      { k: "Luni", v: "08:00 – 20:00" },
      { k: "Marți", v: "08:00 – 20:00" },
      { k: "Miercuri", v: "08:00 – 20:00" },
      { k: "Joi", v: "08:00 – 20:00" },
      { k: "Vineri", v: "08:00 – 20:00" },
      { k: "Sâmbătă", v: "08:00 – 19:00" },
      { k: "Duminică", v: "09:00 – 19:00" },
    ],
    [],
  );

  const { addToCart } = useCart();
  const [modalCake, setModalCake] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const { data, loading, error } = useQuery(GET_MENU, {
    variables: { categorii: CATEGORY_ORDER },
    fetchPolicy: "cache-first",
  });

  const categories = useMemo(() => data?.categoriesWithCount ?? [], [data]);
  const productsRaw = useMemo(() => data?.productsByCategories ?? [], [data]);

  const products = useMemo(
    () =>
      (productsRaw ?? []).map((p) => ({
        id: p.id,
        categorie: p.categorie,
        nume: p.nume,
        pret: p.pret ?? null,
        descriere: p.descriere ?? "",
        descriere_lunga: p.descriere_lunga ?? "",
        ingrediente: p.ingrediente ?? "",
        alergeni: p.alergeni ?? "",
        calorii_kcal: p.calorii_kcal ?? null,
        proteine_g: p.proteine_g ?? null,
        carbohidrati_g: p.carbohidrati_g ?? null,
        grasimi_g: p.grasimi_g ?? null,
        sare_g: p.sare_g ?? null,
        zaharuri_g: p.zaharuri_g ?? null,
        reducere_procent: p.reducere_procent ?? 0,
        reducere_tip: p.reducere_tip ?? "procent",
        pret_redus: p.pret_redus ?? null,
        imagine: p.imagine ?? null,
        imagini: Array.isArray(p.imagini) ? p.imagini : [],
        disponibil: p.disponibil ?? true,
        stoc: p.stoc ?? 0,
        tip_pret: p.tip_pret ?? "fix",
        gramaj: p.gramaj ?? null,
        gramaj_selectabil: p.gramaj_selectabil ?? false,
        gramaj_min: p.gramaj_min ?? null,
        gramaj_max: p.gramaj_max ?? null,
        pas_gramaj: p.pas_gramaj ?? null,
        termen_ore: p.termen_ore ?? 0,
        permite_mentiuni: p.permite_mentiuni ?? true,
        este_tort_personalizat: p.este_tort_personalizat ?? false,
        afiseaza_pret_variabil: p.afiseaza_pret_variabil ?? false,
        afiseaza_per_buc: p.afiseaza_per_buc ?? false,
      })),
    [productsRaw],
  );

  const autoScrollRef = useRef(false);
  const unlockTimerRef = useRef(null);
  const OFFSET = 68 + 56 + 10;

  const getDefaultActiveId = (categoriesList = []) => {
    if (!categoriesList.length) return null;

    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    return isMobile ? categoriesList[0].id : "__all__";
  };

  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) return;

    window.history.scrollRestoration = "manual";

    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });

    return () => {
      window.history.scrollRestoration = "auto";
    };
  }, []);

  useEffect(() => {
    if (!activeId && categories.length) {
      setActiveId(getDefaultActiveId(categories));
    }
  }, [categories, activeId]);

  useEffect(() => {
    const targetCategoryId = location.state?.targetCategoryId;
    if (loading) return;
    if (!categories.length) return;
    if (!targetCategoryId) return;

    setActiveId(targetCategoryId);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToCategory(targetCategoryId);
      });
    });
  }, [location.state, loading, categories]);

  useEffect(() => {
    if (loading) return;
    if (!categories.length) return;
    if (!location.hash) return;

    const targetId = location.hash.replace("#", "");
    const catId = targetId.replace("cat-", "");

    setActiveId(catId);
  }, [location.hash, loading, categories]);

  useEffect(() => {
    if (loading) return;
    if (!categories.length) return;
    if (!location.hash) return;
    if (!activeId) return;

    const targetId = location.hash.replace("#", "");

    let attempts = 0;
    const maxAttempts = 20;

    const scrollToHashTarget = () => {
      const el = document.getElementById(targetId);

      if (!el) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(scrollToHashTarget, 80);
        }
        return;
      }

      const y = el.getBoundingClientRect().top + window.scrollY;

      autoScrollRef.current = true;

      window.scrollTo({
        top: Math.max(0, y - OFFSET),
        behavior: "smooth",
      });

      clearTimeout(unlockTimerRef.current);
      unlockTimerRef.current = setTimeout(() => {
        autoScrollRef.current = false;
      }, 650);
    };

    requestAnimationFrame(() => {
      scrollToHashTarget();
    });
  }, [location.hash, activeId, loading, categories, OFFSET]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeId) ?? null,
    [activeId, categories],
  );

  const handleAddToCart = (product) => {
    if (product.este_tort_personalizat) {
      setModalCake(product);
      return;
    }

    addToCart(product, {
      cantitate: 1,
      gramaj: product.gramaj_selectabil ? "" : product.gramaj,
    });
  };

  const scrollToCategory = (id) => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    const realId = id === "__all__" ? null : id;

    let selector = "#produse";

    if (isMobile) {
      selector = realId ? `#cat-${realId}` : "#produse";
    }

    const el = document.querySelector(selector);
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY;

    autoScrollRef.current = true;

    window.scrollTo({
      top: Math.max(0, y - OFFSET),
      behavior: "smooth",
    });

    clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = setTimeout(() => {
      autoScrollRef.current = false;
    }, 650);
  };

  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) return;

    const sections = (categories ?? [])
      .filter((c) => c?.id && c.id !== "__all__")
      .map((c) => document.getElementById(`cat-${c.id}`))
      .filter(Boolean);

    if (!sections.length) return;

    const handleScroll = () => {
      if (autoScrollRef.current) return;

      const scrollPos = window.scrollY + OFFSET + 20;
      let currentId = sections[0].id.replace("cat-", "");

      for (let i = 0; i < sections.length; i++) {
        const el = sections[i];
        if (el.offsetTop <= scrollPos) currentId = el.id.replace("cat-", "");
      }

      setActiveId(currentId);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(unlockTimerRef.current);
    };
  }, [OFFSET, categories]);

  return (
    <PageWrap id="meniu">
      <MenuBar
        categories={categories}
        activeId={activeId}
        onSelect={(id) => {
          setActiveId(id);
          scrollToCategory(id);
        }}
      />

      <HeroCarousel
        onCta={(targetCatId) => {
          setActiveId(targetCatId);
          scrollToCategory(targetCatId);
        }}
      />

      <MenuLayout>
        <DesktopOnly>
          <LeftCol>
            <CategorySidebar
              categories={categories}
              activeId={activeId}
              onSelect={(id) => {
                setActiveId(id);
                scrollToCategory(id);
              }}
            />
          </LeftCol>
        </DesktopOnly>

        <RightCol id="produse">
          {error ? (
            <div style={{ padding: 16 }}>
              Eroare la încărcare meniu. Încearcă refresh.
            </div>
          ) : (
            <CategoryProducts
              categories={categories}
              products={products}
              activeId={activeId}
              modeDesktop="filter"
              modeMobile="all"
              onAddToCart={handleAddToCart}
              onOpenProduct={(product) => setSelectedProduct(product)}
            />
          )}
        </RightCol>
      </MenuLayout>

      {modalCake && (
        <CustomCakeModal
          product={modalCake}
          onClose={() => setModalCake(null)}
          onConfirm={(options) => {
            addToCart(modalCake, options);
            setModalCake(null);
          }}
        />
      )}

      {selectedProduct && (
        <ProductDetailsModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      <IngredientsSection />

      <ContactInfo
        brand={brand}
        address={address}
        mapsUrl={mapsUrl}
        phones={[phone]}
        email={email}
        schedule={schedule}
        note="Pentru comenzi personalizate, lasă-ne un mesaj cu data evenimentului."
      />
    </PageWrap>
  );
}
